import { Label } from '@/components/ui/shadcn/label';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import { Setting } from '@/types';

type NavigationVisibilityKey =
    | 'home'
    | 'analytics'
    | 'colleagues'
    | 'companies'
    | 'contacts'
    | 'leads'
    | 'projects'
    | 'workOrders'
    | 'purchaseOrders'
    | 'knowledgeBase'
    | 'serviceRequests'
    | 'vacations';

const NAV_ITEMS: Array<{
    key: NavigationVisibilityKey;
    labelKey: string;
    fallbackLabel: string;
}> = [
    { key: 'home', labelKey: 'navigation.home', fallbackLabel: 'Home' },
    { key: 'analytics', labelKey: 'navigation.analytics', fallbackLabel: 'Analytics' },
    { key: 'colleagues', labelKey: 'navigation.colleagues', fallbackLabel: 'Colleagues' },
    { key: 'companies', labelKey: 'navigation.companies', fallbackLabel: 'Companies' },
    { key: 'contacts', labelKey: 'navigation.contacts', fallbackLabel: 'Contacts' },
    { key: 'leads', labelKey: 'navigation.leads', fallbackLabel: 'Leads' },
    { key: 'projects', labelKey: 'navigation.projects', fallbackLabel: 'Projects' },
    { key: 'workOrders', labelKey: 'navigation.work_orders', fallbackLabel: 'Service work orders' },
    { key: 'purchaseOrders', labelKey: 'navigation.purchase_orders', fallbackLabel: 'Purchase Orders' },
    { key: 'knowledgeBase', labelKey: 'navigation.knowledge_base', fallbackLabel: 'Knowledge Base' },
    { key: 'serviceRequests', labelKey: 'navigation.service_requests', fallbackLabel: 'Service Requests' },
    { key: 'vacations', labelKey: 'navigation.vacations', fallbackLabel: 'Vacations' },
];

interface NavigationVisibilityEditorProps {
    setting: Setting;
    onUpdate: (key: string, value: Record<string, boolean>) => void;
}

export default function NavigationVisibilityEditor({ setting, onUpdate }: NavigationVisibilityEditorProps) {
    const { t } = useTranslations();

    const currentValue = (setting.value ?? {}) as Partial<Record<NavigationVisibilityKey, boolean>>;

    const handleToggle = (navKey: NavigationVisibilityKey, isVisible: boolean) => {
        const nextValue: Record<string, boolean> = {
            ...currentValue,
            [navKey]: isVisible,
        };

        onUpdate(setting.key, nextValue);
    };

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <p className="text-muted-foreground text-sm">
                    {t('settings.navigation_visibility.description', 'Choose which items appear in the main navigation for all users.')}
                </p>
            </div>

            <div className="space-y-3">
                {NAV_ITEMS.map((item) => {
                    const checked = currentValue[item.key] ?? true;
                    const switchId = `nav-visibility-${item.key}`;

                    return (
                        <div className="flex items-center justify-between gap-4" key={item.key}>
                            <Label htmlFor={switchId} className="text-sm font-medium">
                                {t(item.labelKey, item.fallbackLabel)}
                            </Label>
                            <Switch id={switchId} checked={checked} onCheckedChange={(nextChecked) => handleToggle(item.key, nextChecked)} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
